import { extractErrorMessage } from "../utils/acceptSuggestions";
import React, { useState, useEffect, useCallback } from "react";
import { formatTier, tierKey } from "../lib/tierLabel";
import { useAuth } from "../context/AuthContext";
import AISuggestionsBanner from "../components/AISuggestionsBanner";
import { persistAcceptedSuggestions, suggestionName } from "../utils/acceptSuggestions";
import { useNavigate, useSearchParams } from "react-router-dom";
import StepDialog from "../components/StepDialog";
import ReadinessBadge from "../components/ReadinessBadge";
import AIPowerSearch from "../components/AIPowerSearch";
import FederatedFromBadge from "../components/FederatedFromBadge";
import UserPicker from "../components/UserPicker";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import {
  Package,
  Server,
  Building2,
  Users,
  Truck,
  Database,
  Settings,
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  Plus,
  Eye,
  Trash2,
  FileText,
  Zap,
  Shield,
  Clock,
  TrendingUp,
  ArrowUpRight,
  ArrowDownRight,
  Link2,
  Unlink,
  Download,
  Network,
  Lock,
  MousePointer,
  MapPin,
  Map,
  Columns,
  ArrowUpDown,
  ArrowUp,
  Layers,
  ArrowDown,
  X,
  Check,
  Sparkles,
} from "lucide-react";
import DependencyDetailPanel from "../components/DependencyDetailPanel";
import DependencyGraph from "../components/DependencyGraph";
import DependencyHealthScore from "../components/DependencyHealthScore";
import AssetInventoryMap from "../components/AssetInventoryMap";

const ASSET_TYPES = [
  { value: "process", label: "Business Process", icon: TrendingUp, color: "bg-blue-100 text-blue-700" },
  { value: "it_system", label: "IT System", icon: Server, color: "bg-purple-100 text-purple-700" },
  { value: "facility", label: "Facility", icon: Building2, color: "bg-amber-100 text-amber-700" },
  { value: "vendor", label: "Vendor/Supplier", icon: Truck, color: "bg-emerald-100 text-emerald-700" },
  { value: "people", label: "People/Team", icon: Users, color: "bg-pink-100 text-pink-700" },
  { value: "data", label: "Data/Information", icon: Database, color: "bg-cyan-100 text-cyan-700" },
  { value: "equipment", label: "Equipment", icon: Settings, color: "bg-slate-100 text-slate-700" },
  { value: "application", label: "Business Application", icon: Layers, color: "bg-cyan-100 text-cyan-700" },
];

const TIER_COLORS = {
  tier_1: "bg-red-100 text-red-700 border-red-200",
  tier_2: "bg-amber-100 text-amber-700 border-amber-200",
  tier_3: "bg-emerald-100 text-emerald-700 border-emerald-200",
};

const TIER_LABELS = {
  tier_1: "Criticality Tier 1 (Critical)",
  tier_2: "Criticality Tier 2 (Important)",
  tier_3: "Criticality Tier 3 (Standard)",
};

export default function AssetInventoryPage() {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [assets, setAssets] = useState([]);
  const [summary, setSummary] = useState(null);
  const [gaps, setGaps] = useState([]);
  const [planMapping, setPlanMapping] = useState(null);
  const [plans, setPlans] = useState([]);
  
  // Dependency Graph state
  const [graphData, setGraphData] = useState({ nodes: [], edges: [], summary: {} });
  const [graphLoading, setGraphLoading] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  
  // Location state for Task 2
  const [locations, setLocations] = useState([]);
  const [mapExpanded, setMapExpanded] = useState(false);
  const [filteredByMap, setFilteredByMap] = useState(null);
  
  // Filters
  const [filterType, setFilterType] = useState("all");
  const [filterTier, setFilterTier] = useState("all");
  const [filterGap, setFilterGap] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [aiSearchResults, setAiSearchResults] = useState(null);
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showPlanDialog, setShowPlanDialog] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [createStep, setCreateStep] = useState(0);
  
  // Dependency Detail Panel state
  const [showDependencyPanel, setShowDependencyPanel] = useState(false);
  const [selectedDependencyItem, setSelectedDependencyItem] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    asset_name: "",
    asset_type: "process",
    description: "",
    owner_name: "",
    owner_email: "",
    // V22.9.7 — Extended ownership: hybrid (free-text Owner + stakeholder pickers)
    primary_owner: null,      // { id, name, email, role }
    secondary_owner: null,    // { id, name, email, role }
    approver: null,           // { id, name, email, role }
    approver_role_hint: "",   // auto-suggested role label (display only)
    department: "",
    location: "",
    location_id: "", // NEW: Mandatory location reference
    manual_tier: "tier_2",
    tags: [],
  });

  // V22.9.7 — Approver role auto-suggest map (asset_type → preferred RBAC role)
  const APPROVER_ROLE_BY_ASSET_TYPE = {
    process: { role: "process_manager", label: "Process Manager" },
    application: { role: "app_manager", label: "App Manager" },
    it_system: { role: "it_function_manager", label: "IT Function Manager" },
    facility: { role: "bcm_manager", label: "BCM Manager" },
    vendor: { role: "bcm_manager", label: "BCM Manager" },
    people: { role: "department_head", label: "Department Head" },
    data: { role: "bcm_manager", label: "BCM Manager" },
    equipment: { role: "bcm_manager", label: "BCM Manager" },
  };

  // Approver auto-suggest: when asset_type changes AND no approver picked yet,
  // populate the role hint. The hint also restricts the Approver picker
  // dropdown — but the user can clear & override.
  useEffect(() => {
    const hint = APPROVER_ROLE_BY_ASSET_TYPE[formData.asset_type];
    setFormData((prev) =>
      prev.approver_role_hint === (hint?.role || "")
        ? prev
        : { ...prev, approver_role_hint: hint?.role || "" }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.asset_type]);

  
  // Column configuration - all available database attributes for assets
  const ALL_ASSET_ATTRIBUTES = [
    { key: "asset_name", name: "Asset Name", type: "text", default: true },
    { key: "asset_type", name: "Type", type: "dropdown", options: ["process", "system", "data", "facility", "equipment", "people"], default: true },
    { key: "criticality_tier", name: "Criticality Tier", type: "dropdown", options: ["tier_1", "tier_2", "tier_3", "tier_4"], default: true },
    { key: "dept_id", name: "Dept ID", type: "text", default: true },
    { key: "process_code", name: "Process ID", type: "text", default: true },
    { key: "app_id", name: "App ID", type: "text", default: false },
    { key: "it_function_id", name: "IT Function ID", type: "text", default: false },
    { key: "owner_name", name: "Owner", type: "text", default: true },
    { key: "location", name: "Location", type: "text", default: false },
    { key: "department", name: "Department", type: "text", default: false },
    { key: "description", name: "Description", type: "text", default: false },
    { key: "owner_email", name: "Owner Email", type: "email", default: false },
    { key: "rto", name: "RTO", type: "text", default: false },
    { key: "rpo", name: "RPO", type: "text", default: false },
    { key: "mtpd", name: "MTPD", type: "text", default: false },
    { key: "status", name: "Status", type: "dropdown", options: ["active", "inactive", "pending"], default: false },
    { key: "tags", name: "Tags", type: "text", default: false },
    { key: "created_at", name: "Created Date", type: "date", default: false },
    { key: "updated_at", name: "Last Updated", type: "date", default: false },
  ];
  
  // Visible columns state (which attributes to show)
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem('asset_visible_columns');
    if (saved) return JSON.parse(saved);
    return ALL_ASSET_ATTRIBUTES.filter(a => a.default).map(a => a.key);
  });
  
  // Sorting state
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  
  // Column filters state
  const [columnFilters, setColumnFilters] = useState({});
  
  const [showColumnDialog, setShowColumnDialog] = useState(false);
  
  // Application Profile linking state
  const [applicationProfiles, setApplicationProfiles] = useState([]);
  const [showLinkProfileDialog, setShowLinkProfileDialog] = useState(false);
  const [assetToLink, setAssetToLink] = useState(null);
  const [linking, setLinking] = useState(false);

  // Access Control - BCM Manager only
  const isBCMAdmin = user?.role === "bcm_manager" || user?.role === "department_head" || user?.role === "admin" || user?.role === "it_drp_manager";

  // Handle node click from dependency graph
  const handleNodeClick = useCallback((node) => {
    if (node?.data) {
      setSelectedDependencyItem({
        id: node.id,
        asset_name: node.data.label,
        tier: node.data.tier,
        asset_type: node.data.nodeType,
        ...node.data
      });
      setShowDependencyPanel(true);
    }
  }, []);

  // Handle asset row click
  const handleAssetRowClick = useCallback((asset) => {
    if (asset?.linked_process_id) {
      setSelectedDependencyItem({
        ...asset,
        id: asset.linked_process_id
      });
      setShowDependencyPanel(true);
    } else {
      // For assets without linked process, show basic info
      setSelectedDependencyItem(asset);
      setShowDependencyPanel(true);
    }
  }, []);

  // Redirect non-admins
  useEffect(() => {
    if (!loading && !isBCMAdmin) {
      toast.error("Access denied. BCM Manager privileges required.");
      navigate("/dashboard");
    }
  }, [loading, isBCMAdmin, navigate]);

  // Auto-open wizard from demo walkthrough
  useEffect(() => {
    if (searchParams.get("autoWizard") === "true" && !loading && assets.length >= 0) {
      const timer = setTimeout(() => {
        setShowCreateDialog(true);
        setSearchParams({}, { replace: true });
      }, 800);
      return () => clearTimeout(timer);
    }
  }, [searchParams, loading, assets.length, setSearchParams]);

  // Persist accepted AI application suggestions via the SAME endpoint the manual
  // "Add Asset" dialog uses (POST /assets/). Owner falls back to the acting user
  // (owner_user_id is required by the backend). Criticality maps to the manual
  // tier; provenance is tagged in custom_attributes.source = "ai_suggestion".
  const _CRIT_TIER = { critical: "tier_1", important: "tier_2", standard: "tier_3" };
  const handleAcceptApplications = async (items) => {
    const result = await persistAcceptedSuggestions({
      items,
      existingKeys: assets.map((a) => a.asset_name),
      keyOf: (raw) => suggestionName(raw),
      createOne: async (raw) => {
        const o = raw && typeof raw === "object" ? raw : {};
        const tier = _CRIT_TIER[String(o.criticality || "").toLowerCase()] || "tier_2";
        const notes = [
          o.type && `Type: ${o.type}`,
          o.recovery_tier && `Recovery tier: ${o.recovery_tier}`,
          o.shared_responsibility_note,
        ].filter(Boolean).join(" · ");
        await api.post("/assets/", {
          asset_name: suggestionName(raw),
          asset_type: "application",
          description: notes || `Application: ${suggestionName(raw)}`,
          owner_user_id: user?.id || "",
          owner_name: user ? `${user.first_name || ""} ${user.last_name || ""}`.trim() : "",
          owner_email: user?.email || "",
          department: o.department || "",
          manual_tier: tier,
          custom_attributes: { source: "ai_suggestion" },
        });
      },
    });
    await fetchAssets();
    return result;
  };


  const fetchAssets = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterType !== "all") params.append("asset_type", filterType);
      if (filterTier !== "all") params.append("tier", filterTier);
      if (filterGap === "yes") params.append("has_gap", "true");
      if (filterGap === "no") params.append("has_gap", "false");
      
      const response = await api.get(`/assets/?${params.toString()}`);
      setAssets(response.data.assets || []);
      setSummary(response.data.summary || null);
    } catch (error) {
      console.error("Error fetching assets:", error);
    }
  }, [api, filterType, filterTier, filterGap]);

  const fetchGaps = useCallback(async () => {
    try {
      const response = await api.get("/assets/gaps");
      setGaps(response.data.gaps || []);
    } catch (error) {
      console.error("Error fetching gaps:", error);
    }
  }, [api]);

  const fetchPlanMapping = useCallback(async () => {
    try {
      const response = await api.get("/multi-plans/asset-to-plan-mapping");
      setPlanMapping(response.data);
    } catch (error) {
      console.error("Error fetching plan mapping:", error);
    }
  }, [api]);

  const fetchPlans = useCallback(async () => {
    try {
      const response = await api.get("/multi-plans/");
      setPlans(response.data.plans || []);
    } catch (error) {
      console.error("Error fetching plans:", error);
    }
  }, [api]);

  const fetchDependencyGraph = useCallback(async () => {
    setGraphLoading(true);
    try {
      const response = await api.get("/dependencies/graph");
      setGraphData({
        nodes: response.data.nodes || [],
        edges: response.data.edges || [],
        summary: response.data.summary || {},
      });
    } catch (error) {
      console.error("Error fetching dependency graph:", error);
      toast.error("Failed to load dependency graph");
    } finally {
      setGraphLoading(false);
    }
  }, [api]);

  // Fetch application profiles for linking
  const fetchApplicationProfiles = useCallback(async () => {
    try {
      const response = await api.get("/application-profile/?status=completed");
      setApplicationProfiles(response.data?.data?.profiles || []);
    } catch (error) {
      console.error("Error fetching application profiles:", error);
      setApplicationProfiles([]);
    }
  }, [api]);

  // Fetch locations for geographic intelligence
  const fetchLocations = useCallback(async () => {
    try {
      const response = await api.get("/bcm-admin/locations");
      setLocations(Array.isArray(response.data) ? response.data : (response.data?.locations || []));
    } catch (error) {
      console.error("Error fetching locations:", error);
    }
  }, [api]);

  // Link asset to application profile
  const handleLinkToProfile = async (profileId) => {
    if (!assetToLink) return;
    setLinking(true);
    try {
      await api.post(`/assets/${assetToLink.id}/link-to-application-profile`, {
        profile_id: profileId
      });
      toast.success(`Asset linked to application profile successfully`);
      setShowLinkProfileDialog(false);
      setAssetToLink(null);
      await fetchAssets();
    } catch (error) {
      console.error("Error linking asset:", error);
      toast.error("Failed to link asset to application profile");
    } finally {
      setLinking(false);
    }
  };

  // Sync asset data from application profile
  const handleSyncFromProfile = async (assetId, profileId) => {
    try {
      await api.post(`/assets/${assetId}/sync-from-application-profile/${profileId}`);
      toast.success("Asset synced with application profile data");
      await fetchAssets();
    } catch (error) {
      console.error("Error syncing asset:", error);
      toast.error("Failed to sync asset data");
    }
  };

  const handleDownloadReport = async () => {
    setDownloadingPdf(true);
    try {
      const response = await api.get("/assets/report/pdf", {
        responseType: "blob",
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Asset_Inventory_Report_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("PDF report downloaded successfully");
    } catch (error) {
      console.error("Error downloading report:", error);
      toast.error("Failed to download PDF report");
    } finally {
      setDownloadingPdf(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchAssets(), fetchGaps(), fetchPlanMapping(), fetchPlans(), fetchDependencyGraph(), fetchApplicationProfiles(), fetchLocations()]);
      setLoading(false);
    };
    if (isBCMAdmin) {
      loadData();
    } else {
      // Non-admin users: set loading to false so redirect can happen
      setLoading(false);
    }
  }, [fetchAssets, fetchGaps, fetchPlanMapping, fetchPlans, fetchDependencyGraph, fetchApplicationProfiles, fetchLocations, isBCMAdmin]);

  // Save visible columns to localStorage
  useEffect(() => {
    localStorage.setItem('asset_visible_columns', JSON.stringify(visibleColumns));
  }, [visibleColumns]);

  // Toggle column visibility
  const toggleColumn = (key) => {
    setVisibleColumns(prev => {
      if (prev.includes(key)) {
        return prev.filter(k => k !== key);
      } else {
        return [...prev, key];
      }
    });
  };

  // Sort handler
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Filter handler
  const handleColumnFilter = (key, value) => {
    setColumnFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Clear all filters
  const clearAllFilters = () => {
    setColumnFilters({});
    setSearchTerm("");
    setFilterType("all");
    setFilterTier("all");
    setFilterGap("all");
  };

  // Get sorted and filtered data
  const getSortedFilteredAssets = (data) => {
    let result = [...data];
    
    // Apply column filters
    Object.keys(columnFilters).forEach(key => {
      const filterValue = columnFilters[key];
      if (filterValue && filterValue.trim()) {
        result = result.filter(item => {
          const value = item[key] || '';
          return String(value).toLowerCase().includes(filterValue.toLowerCase());
        });
      }
    });
    
    // Apply search term
    if (searchTerm) {
      result = result.filter(a => 
        a.asset_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        a.owner_name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Apply type filter
    if (filterType !== "all") {
      result = result.filter(a => a.asset_type === filterType);
    }
    
    // Apply tier filter
    if (filterTier !== "all") {
      result = result.filter(a => a.criticality_tier === filterTier);
    }
    
    // Apply sorting
    if (sortConfig.key) {
      result.sort((a, b) => {
        const aVal = a[sortConfig.key] || '';
        const bVal = b[sortConfig.key] || '';
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    
    return result;
  };

  const handleSyncFromBIA = async () => {
    setSyncing(true);
    try {
      const response = await api.post("/assets/sync-from-bia");
      toast.success(`Synced ${response.data.created} new assets, updated ${response.data.updated}`);
      await Promise.all([fetchAssets(), fetchGaps(), fetchPlanMapping()]);
    } catch (error) {
      toast.error("Failed to sync assets from BIA");
    } finally {
      setSyncing(false);
    }
  };

  const handleCreateAsset = async () => {
    try {
      // V22.9.7 — Flatten picker objects into the AssetCreate payload shape
      const payload = {
        ...formData,
        // owner_user_id is required by the backend — fall back to webadmin/self if
        // no Primary Owner is picked (keeps backwards compat with V14 contract).
        owner_user_id:
          formData.primary_owner?.id || formData.owner_user_id || user?.id || "",
        // Owner block (free-text the user typed)
        owner_name: formData.owner_name || formData.primary_owner?.name || "",
        owner_email: formData.owner_email || formData.primary_owner?.email || "",
        // Primary owner (picker)
        primary_owner_user_id: formData.primary_owner?.id || null,
        primary_owner_name: formData.primary_owner?.name || null,
        primary_owner_email: formData.primary_owner?.email || null,
        // Secondary owner (picker)
        secondary_owner_user_id: formData.secondary_owner?.id || null,
        secondary_owner_name: formData.secondary_owner?.name || null,
        secondary_owner_email: formData.secondary_owner?.email || null,
        // Approver (picker — auto-suggested role)
        approver_user_id: formData.approver?.id || null,
        approver_name: formData.approver?.name || null,
        approver_email: formData.approver?.email || null,
        approver_role: formData.approver?.role || formData.approver_role_hint || null,
      };
      // Strip helper-only keys the backend doesn't accept
      delete payload.primary_owner;
      delete payload.secondary_owner;
      delete payload.approver;
      delete payload.approver_role_hint;

      await api.post("/assets/", payload);
      toast.success("Asset created successfully");
      setShowCreateDialog(false);
      resetForm();
      await fetchAssets();
    } catch (error) {
      toast.error(extractErrorMessage(error, "Failed to create asset"));
    }
  };

  const handleDeleteAsset = async (assetId) => {
    if (!window.confirm("Are you sure you want to delete this asset?")) return;
    try {
      await api.delete(`/assets/${assetId}`);
      toast.success("Asset deleted");
      await Promise.all([fetchAssets(), fetchGaps(), fetchPlanMapping()]);
    } catch (error) {
      toast.error("Failed to delete asset");
    }
  };

  const handleGeneratePlan = async (planType) => {
    if (!selectedAsset) return;
    setGenerating(true);
    try {
      const response = await api.post("/multi-plans/generate", {
        plan_type: planType,
        asset_ids: [selectedAsset.id],
        include_dependencies: true,
      });
      toast.success(`${planType.toUpperCase()} plan generated successfully`);
      setShowPlanDialog(false);
      await Promise.all([fetchAssets(), fetchGaps(), fetchPlanMapping(), fetchPlans()]);
    } catch (error) {
      toast.error("Failed to generate plan");
    } finally {
      setGenerating(false);
    }
  };

  const resetForm = () => {
    setFormData({
      asset_name: "",
      asset_type: "process",
      description: "",
      owner_name: "",
      owner_email: "",
      primary_owner: null,
      secondary_owner: null,
      approver: null,
      approver_role_hint: "",
      department: "",
      location: "",
      manual_tier: "tier_2",
      tags: [],
    });
  };

  const getAssetIcon = (type) => {
    const assetType = ASSET_TYPES.find(t => t.value === type);
    return assetType?.icon || Package;
  };

  const getAssetColor = (type) => {
    const assetType = ASSET_TYPES.find(t => t.value === type);
    return assetType?.color || "bg-slate-100 text-slate-700";
  };

  const filteredAssets = (aiSearchResults || assets).filter(asset => {
    if (!aiSearchResults && searchTerm) {
      const search = searchTerm.toLowerCase();
      // Also match the asset_type value ("people") and its friendly label
      // ("People/Team") so typing "people" or "team" returns every matching
      // asset — not just those whose name happens to contain the word.
      const typeLabel = (ASSET_TYPES.find(t => t.value === asset.asset_type)?.label || "").toLowerCase();
      return (
        asset.asset_name?.toLowerCase().includes(search) ||
        asset.department?.toLowerCase().includes(search) ||
        asset.owner_name?.toLowerCase().includes(search) ||
        asset.owner_email?.toLowerCase().includes(search) ||
        asset.location?.toLowerCase().includes(search) ||
        asset.asset_type?.toLowerCase().includes(search) ||
        typeLabel.includes(search) ||
        (asset.tags || []).some(t => (t || "").toLowerCase().includes(search))
      );
    }
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-sky-700" />
      </div>
    );
  }

  // Access denied screen for non-admins
  if (!isBCMAdmin) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4" data-testid="access-denied">
        <Lock className="w-16 h-16 text-red-400" />
        <h2 className="text-xl font-semibold text-slate-700">Access Restricted</h2>
        <p className="text-slate-500">This page is only accessible to BCM Manageristrators.</p>
        <Button onClick={() => navigate("/dashboard")}>Return to Dashboard</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="asset-inventory-page">
      <AISuggestionsBanner module="infrastructure_applications" onAccept={handleAcceptApplications} />
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Asset Inventory</h1>
          <p className="text-slate-500 mt-1">
            Centralized registry linking BIA to Risk Assessment with automatic criticality mapping
          </p>
          <div className="mt-2">
            <ReadinessBadge
              total={assets.length}
              complete={assets.filter(a => a.location_id && a.owner_name).length}
              label="Assets Registered"
              completeLabel={`of ${assets.length} have both a location and an owner`}
            />
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setShowColumnDialog(true)}
            data-testid="manage-columns-btn"
          >
            <Columns className="w-4 h-4 mr-2" />
            Manage Columns
          </Button>
          <Button
            variant="outline"
            onClick={handleSyncFromBIA}
            disabled={syncing}
            data-testid="sync-from-bia-btn"
          >
            {syncing ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Sync from BIA
          </Button>
          <Button 
            variant="outline" 
            onClick={handleDownloadReport}
            disabled={downloadingPdf}
            data-testid="download-pdf-btn"
          >
            {downloadingPdf ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            Export PDF
          </Button>
          <Button onClick={() => setShowCreateDialog(true)} data-testid="add-asset-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Asset
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase">Total Assets</p>
                <p className="text-2xl font-bold text-slate-900" data-testid="total-assets-count">{summary?.by_tier ? Object.values(summary.by_tier).reduce((a, b) => a + (b || 0), 0) : 0}</p>
                {summary?.by_tier && (() => {
                  const bt = summary.by_tier;
                  const untiered = Object.entries(bt).reduce((acc, [k, v]) => (["tier_1", "tier_2", "tier_3"].includes(k) ? acc : acc + (v || 0)), 0);
                  return untiered > 0 ? (
                    <p className="text-[10px] text-amber-600 mt-0.5" data-testid="untiered-assets-count">{untiered} untiered</p>
                  ) : null;
                })()}
              </div>
              <Package className="w-8 h-8 text-sky-700" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-red-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-red-600 uppercase">Criticality Tier 1 (Critical)</p>
                <p className="text-2xl font-bold text-red-700">{summary?.by_tier?.tier_1 || 0}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-amber-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-amber-600 uppercase">Criticality Tier 2 (Important)</p>
                <p className="text-2xl font-bold text-amber-700">{summary?.by_tier?.tier_2 || 0}</p>
              </div>
              <Clock className="w-8 h-8 text-amber-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-emerald-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-emerald-600 uppercase">Criticality Tier 3 (Standard)</p>
                <p className="text-2xl font-bold text-emerald-700">{summary?.by_tier?.tier_3 || 0}</p>
              </div>
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
          </CardContent>
        </Card>

        <Card className={gaps.length > 0 ? "border-red-300 bg-red-50" : "border-emerald-200"}>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase" style={{ color: gaps.length > 0 ? '#dc2626' : '#059669' }}>
                  Resilience Gaps
                </p>
                <p className="text-2xl font-bold" style={{ color: gaps.length > 0 ? '#dc2626' : '#059669' }}>
                  {gaps.length}
                </p>
              </div>
              {gaps.length > 0 ? (
                <Unlink className="w-8 h-8 text-red-600" />
              ) : (
                <Link2 className="w-8 h-8 text-emerald-600" />
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Resilience Gaps Alert */}
      {gaps.length > 0 && (
        <Card className="border-red-300 bg-red-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2 text-red-800">
              <AlertTriangle className="w-5 h-5" />
              Resilience Gaps Detected
            </CardTitle>
            <CardDescription className="text-red-700">
              {gaps.length} Criticality Tier 1 (Critical) assets have no associated BCP or IT-DRP plans
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {gaps.slice(0, 5).map(gap => (
                <Badge key={gap.id} variant="destructive" className="cursor-pointer" onClick={() => {
                  setSelectedAsset(gap);
                  setShowPlanDialog(true);
                }}>
                  {gap.asset_name} ({gap.rto_hours}h RTO)
                </Badge>
              ))}
              {gaps.length > 5 && (
                <Badge variant="outline">+{gaps.length - 5} more</Badge>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Content Tabs */}
      <Tabs defaultValue="inventory" className="space-y-4">
        <TabsList>
          <TabsTrigger value="inventory">Asset Inventory</TabsTrigger>
          <TabsTrigger value="geo-map" data-testid="geo-map-tab">
            <Map className="w-4 h-4 mr-1" />
            Geographic Map
          </TabsTrigger>
          <TabsTrigger value="dependency-map" data-testid="dependency-map-tab">
            <Network className="w-4 h-4 mr-1" />
            Dependency Map
          </TabsTrigger>
          <TabsTrigger value="mapping">Asset-to-Plan Mapping</TabsTrigger>
          <TabsTrigger value="plans">Generated Plans ({plans.length})</TabsTrigger>
        </TabsList>

        {/* Geographic Map Tab - NEW for Task 2 */}
        <TabsContent value="geo-map" className="space-y-4">
          <AssetInventoryMap
            assets={assets}
            locations={locations}
            onAssetSelect={(asset) => {
              setSelectedAsset(asset);
              setShowViewDialog(true);
            }}
            onFilteredAssetsChange={(filtered) => setFilteredByMap(filtered)}
            isExpanded={mapExpanded}
            onToggleExpand={() => setMapExpanded(!mapExpanded)}
          />
          
          {/* Filtered Assets from Map Selection */}
          {filteredByMap && filteredByMap.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Assets in Selected Region ({filteredByMap.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Asset</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>Criticality Tier</TableHead>
                      <TableHead>RTO</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredByMap.map((asset) => (
                      <TableRow key={asset.id} className="cursor-pointer hover:bg-slate-50" onClick={() => {
                        setSelectedAsset(asset);
                        setShowViewDialog(true);
                      }}>
                        <TableCell className="font-medium">{asset.asset_name}</TableCell>
                        <TableCell>{asset.asset_type?.replace("_", " ")}</TableCell>
                        <TableCell>
                          {asset.location || locations.find(l => l.id === asset.location_id)?.name || "—"}
                        </TableCell>
                        <TableCell>
                          <Badge className={TIER_COLORS[tierKey(asset.criticality_tier || asset.tier)]}>
                            {TIER_LABELS[tierKey(asset.criticality_tier || asset.tier)] || formatTier(asset.criticality_tier || asset.tier)}
                          </Badge>
                        </TableCell>
                        <TableCell>{asset.rto_hours ? `${asset.rto_hours}h` : "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Dependency Map Tab */}
        <TabsContent value="dependency-map" className="space-y-6">
          {/* Dependency Graph - Full width */}
          <DependencyGraph
            nodes={graphData.nodes}
            edges={graphData.edges}
            summary={graphData.summary}
            loading={graphLoading}
            onRefresh={fetchDependencyGraph}
            onNodeClick={handleNodeClick}
            title="Organization Dependency Map"
            height="700px"
          />
          
          {/* Dependency Health Score - Below the map */}
          <DependencyHealthScore />
        </TabsContent>

        {/* Inventory Tab */}
        <TabsContent value="inventory" className="space-y-4">
          {/* AI Power Search */}
          <AIPowerSearch
            module="assets"
            onResults={(results, interpretation) => setAiSearchResults(results)}
            onClear={() => setAiSearchResults(null)}
            placeholder='Try: "show all critical facilities" or "find servers in data center"...'
          />
          {/* Filters */}
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex-1 min-w-[200px]">
              <Input
                placeholder="Search assets..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="max-w-sm"
              />
            </div>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {ASSET_TYPES.map(type => (
                  <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterTier} onValueChange={setFilterTier}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by Criticality Tier" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Criticality Tiers</SelectItem>
                <SelectItem value="tier_1">Criticality Tier 1 (Critical)</SelectItem>
                <SelectItem value="tier_2">Criticality Tier 2 (Important)</SelectItem>
                <SelectItem value="tier_3">Criticality Tier 3 (Standard)</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterGap} onValueChange={setFilterGap}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by Gap" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Assets</SelectItem>
                <SelectItem value="yes">Has Resilience Gap</SelectItem>
                <SelectItem value="no">No Gap</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Assets Table */}
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Asset Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Dept ID</TableHead>
                  <TableHead>Process ID</TableHead>
                  <TableHead>Owners &amp; Approver</TableHead>
                  <TableHead>Location(s)</TableHead>
                  <TableHead>Criticality Tier / Rating</TableHead>
                  <TableHead>RTO</TableHead>
                  <TableHead>Plans</TableHead>
                  <TableHead>Gap</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredAssets.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} className="text-center py-8 text-slate-500">
                      {assets.length === 0
                        ? `No assets found. Click "Sync from BIA" to populate from BIA assessments.`
                        : `No assets match your filters. ${searchTerm ? `Try a different search term or ` : ""}clear the filters to see all ${assets.length} assets.`}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredAssets.map((asset) => {
                    const IconComponent = getAssetIcon(asset.asset_type);
                    return (
                      <TableRow 
                        key={asset.id} 
                        className="cursor-pointer hover:bg-sky-50/50 transition-colors"
                        onClick={() => handleAssetRowClick(asset)}
                        data-testid={`asset-row-${asset.id}`}
                      >
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className={`p-1.5 rounded ${getAssetColor(asset.asset_type)}`}>
                              <IconComponent className="w-4 h-4" />
                            </div>
                            <div>
                              <div className="font-medium flex items-center gap-1">
                                {asset.asset_name}
                                {asset.linked_process_id && (
                                  <MousePointer className="w-3 h-3 text-sky-500" />
                                )}
                                {asset.source === "onboarding_enrichment" && (
                                  <Badge
                                    variant="outline"
                                    className="bg-violet-50 text-violet-700 border-violet-200 text-[10px] gap-1 whitespace-nowrap"
                                    title="Auto-created from onboarding auto-discovery. Review and complete this asset record."
                                    data-testid={`asset-auto-created-${asset.id}`}
                                  >
                                    <Sparkles className="w-3 h-3" /> Auto-created
                                  </Badge>
                                )}
                                <FederatedFromBadge api={api} sourceProfileId={asset.source_profile_id} />
                              </div>
                              <p className="text-xs text-slate-500">{asset.department || asset.location || "—"}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={getAssetColor(asset.asset_type)}>
                            {ASSET_TYPES.find(t => t.value === asset.asset_type)?.label || asset.asset_type}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {asset.dept_id ? (
                            <Badge variant="outline" className="bg-sky-50 text-sky-700 border-sky-200 text-xs font-mono">
                              {asset.dept_id}
                            </Badge>
                          ) : <span className="text-slate-400 text-xs">—</span>}
                        </TableCell>
                        <TableCell>
                          {asset.process_code ? (
                            <Badge variant="outline" className="bg-violet-50 text-violet-700 border-violet-200 text-xs font-mono">
                              {asset.process_code}
                            </Badge>
                          ) : <span className="text-slate-400 text-xs">—</span>}
                        </TableCell>
                        <TableCell>
                          {/* V22.9.7 — Owners & Approver column */}
                          <div className="space-y-1 max-w-[220px] text-xs" data-testid={`owners-approver-${asset.id}`}>
                            {asset.primary_owner_name ? (
                              <div className="flex items-center gap-1">
                                <Badge variant="outline" className="text-[9px] bg-sky-50 text-sky-700 border-sky-200 px-1 py-0">P</Badge>
                                <span className="truncate text-slate-700" title={asset.primary_owner_email}>{asset.primary_owner_name}</span>
                              </div>
                            ) : null}
                            {asset.secondary_owner_name ? (
                              <div className="flex items-center gap-1">
                                <Badge variant="outline" className="text-[9px] bg-slate-50 text-slate-600 border-slate-200 px-1 py-0">S</Badge>
                                <span className="truncate text-slate-700" title={asset.secondary_owner_email}>{asset.secondary_owner_name}</span>
                              </div>
                            ) : null}
                            {asset.approver_name ? (
                              <div className="flex items-center gap-1">
                                <Badge variant="outline" className="text-[9px] bg-violet-50 text-violet-700 border-violet-200 px-1 py-0">A</Badge>
                                <span className="truncate text-slate-700" title={asset.approver_email}>{asset.approver_name}</span>
                                {asset.approver_role ? (
                                  <span className="text-[9px] text-violet-500">·{asset.approver_role.replace(/_/g, " ")}</span>
                                ) : null}
                              </div>
                            ) : null}
                            {!asset.primary_owner_name && !asset.secondary_owner_name && !asset.approver_name && (
                              <span className="text-slate-400 text-xs">—</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1 max-w-[200px]">
                            {asset.locations && asset.locations.length > 0 ? (
                              asset.locations.map((loc, idx) => (
                                <Badge key={idx} variant="outline" className="bg-slate-50 text-slate-700 text-xs">
                                  <MapPin className="w-3 h-3 mr-1" />
                                  {typeof loc === 'string' ? loc : loc.name || loc.location_name}
                                </Badge>
                              ))
                            ) : asset.location ? (
                              <Badge variant="outline" className="bg-slate-50 text-slate-700 text-xs">
                                <MapPin className="w-3 h-3 mr-1" />
                                {asset.location}
                              </Badge>
                            ) : locations.find(l => l.id === asset.location_id)?.name ? (
                              <Badge variant="outline" className="bg-slate-50 text-slate-700 text-xs">
                                <MapPin className="w-3 h-3 mr-1" />
                                {locations.find(l => l.id === asset.location_id)?.name}
                              </Badge>
                            ) : (
                              <span className="text-slate-400 text-xs">—</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Badge className={TIER_COLORS[tierKey(asset.tier)]}>
                              {TIER_LABELS[tierKey(asset.tier)]?.split(" ").slice(0,2).join(" ")}
                            </Badge>
                            <span className="text-sm text-slate-500">Rating: {asset.asset_rating}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {asset.rto_hours ? `${asset.rto_hours}h` : "—"}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {asset.has_bcp && <Badge variant="outline" className="bg-blue-50 text-blue-700">BCP</Badge>}
                            {asset.has_it_drp && <Badge variant="outline" className="bg-purple-50 text-purple-700">IT-DRP</Badge>}
                            {asset.linked_application_profile_id && (
                              <Badge variant="outline" className="bg-violet-50 text-violet-700 border-violet-200">
                                <Link2 className="w-3 h-3 mr-1" />
                                App Profile
                              </Badge>
                            )}
                            {!asset.has_bcp && !asset.has_it_drp && !asset.linked_application_profile_id && <span className="text-slate-400">None</span>}
                          </div>
                        </TableCell>
                        <TableCell>
                          {asset.resilience_gap ? (
                            <Badge variant="destructive">GAP</Badge>
                          ) : (
                            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedAsset(asset);
                                setShowViewDialog(true);
                              }}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            {/* Link to Application Profile */}
                            {asset.asset_type === "it_system" && !asset.linked_application_profile_id && applicationProfiles.length > 0 && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-purple-600"
                                onClick={() => {
                                  setAssetToLink(asset);
                                  setShowLinkProfileDialog(true);
                                }}
                                title="Link to Application Profile"
                              >
                                <Link2 className="w-4 h-4" />
                              </Button>
                            )}
                            {/* Sync from Application Profile */}
                            {asset.linked_application_profile_id && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-purple-600"
                                onClick={() => handleSyncFromProfile(asset.id, asset.linked_application_profile_id)}
                                title="Sync from Application Profile"
                              >
                                <RefreshCw className="w-4 h-4" />
                              </Button>
                            )}
                            {asset.resilience_gap && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-sky-700"
                                onClick={() => {
                                  setSelectedAsset(asset);
                                  setShowPlanDialog(true);
                                }}
                              >
                                <FileText className="w-4 h-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600"
                              onClick={() => handleDeleteAsset(asset.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* Mapping Tab */}
        <TabsContent value="mapping" className="space-y-4">
          {planMapping && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-4 text-center">
                    <p className="text-3xl font-bold text-sky-700">{planMapping.summary?.total_assets || 0}</p>
                    <p className="text-sm text-slate-500">Total Assets</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 text-center">
                    <p className="text-3xl font-bold text-blue-600">{planMapping.summary?.assets_with_bcp || 0}</p>
                    <p className="text-sm text-slate-500">With BCP</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 text-center">
                    <p className="text-3xl font-bold text-purple-600">{planMapping.summary?.assets_with_it_drp || 0}</p>
                    <p className="text-sm text-slate-500">With IT-DRP</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 text-center">
                    <p className="text-3xl font-bold text-red-600">{planMapping.summary?.resilience_gaps || 0}</p>
                    <p className="text-sm text-slate-500">Gaps</p>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>Asset-to-Plan Mapping</CardTitle>
                  <CardDescription>Overview of assets and their associated recovery plans</CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Asset</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Criticality Tier</TableHead>
                        <TableHead>RTO</TableHead>
                        <TableHead>BCP</TableHead>
                        <TableHead>IT-DRP</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {planMapping.mapping?.slice(0, 20).map((item) => (
                        <TableRow key={item.asset_id}>
                          <TableCell className="font-medium">{item.asset_name}</TableCell>
                          <TableCell>{item.asset_type?.replace("_", " ")}</TableCell>
                          <TableCell>
                            <Badge className={TIER_COLORS[item.tier]}>
                              {formatTier(item.tier)}
                            </Badge>
                          </TableCell>
                          <TableCell>{item.rto_hours ? `${item.rto_hours}h` : "—"}</TableCell>
                          <TableCell>
                            {item.has_bcp ? (
                              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                            ) : (
                              <span className="text-slate-400">—</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {item.has_it_drp ? (
                              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                            ) : (
                              <span className="text-slate-400">—</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {item.resilience_gap ? (
                              <Badge variant="destructive">GAP</Badge>
                            ) : (
                              <Badge variant="outline" className="bg-emerald-50 text-emerald-700">Covered</Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Plans Tab */}
        <TabsContent value="plans" className="space-y-4">
          <div className="grid gap-4">
            {plans.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center text-slate-500">
                  No plans generated yet. Select assets with gaps to generate BCP or IT-DRP plans.
                </CardContent>
              </Card>
            ) : (
              plans.map((plan) => (
                <Card key={plan.id}>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Badge className={plan.plan_type === "bcp" ? "bg-blue-100 text-blue-700" : "bg-purple-100 text-purple-700"}>
                          {plan.plan_type?.toUpperCase()}
                        </Badge>
                        <CardTitle className="text-lg">{plan.title}</CardTitle>
                      </div>
                      <Badge variant="outline">{plan.status}</Badge>
                    </div>
                    <CardDescription>
                      Covers {plan.assets_covered?.length || 0} assets • 
                      RTO Range: {plan.rto_summary?.min_rto}h - {plan.rto_summary?.max_rto}h
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {plan.assets_covered?.slice(0, 5).map((asset) => (
                        <Badge key={asset.id} variant="outline">
                          {asset.name} ({formatTier(asset.tier)})
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Create Asset Dialog — TurboTax Progressive Disclosure */}
      <StepDialog
        open={showCreateDialog}
        onOpenChange={(open) => { setShowCreateDialog(open); if (!open) resetForm(); }}
        title="Add New Asset"
        currentStep={createStep}
        onStepChange={setCreateStep}
        onSubmit={handleCreateAsset}
        onCancel={() => setShowCreateDialog(false)}
        submitLabel="Create Asset"
        submitDisabled={!formData.asset_name || !formData.location_id}
        saving={false}
        canAdvance={
          createStep === 0 ? !!formData.asset_name :
          createStep === 1 ? !!formData.asset_type :
          createStep === 2 ? true :
          createStep === 3 ? !!formData.location_id :
          true
        }
        steps={[
          { id: "identity", title: "Identity", subtitle: "What is this asset called and what type is it?", isComplete: !!formData.asset_name && !!formData.asset_type },
          { id: "classification", title: "Classification", subtitle: "How critical is this asset to your business operations?", isComplete: !!formData.manual_tier },
          { id: "ownership", title: "Ownership", subtitle: "Who owns and manages this asset?", isComplete: !!formData.owner_name || !!formData.primary_owner || !!formData.department },
          { id: "location", title: "Location", subtitle: "Where does this asset physically reside?", isComplete: !!formData.location_id },
          { id: "review", title: "Review", subtitle: "Add a description and confirm the details before creating.", isComplete: false },
        ]}
      >
        {/* Step 0: Identity */}
        {createStep === 0 && (
          <div className="space-y-4" data-testid="step-identity">
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Asset Name *</Label>
              <Input
                value={formData.asset_name}
                onChange={(e) => setFormData({ ...formData, asset_name: e.target.value })}
                placeholder="e.g., Payment Gateway Server"
                data-testid="asset-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Asset Type *</Label>
              <div className="space-y-2">
                {ASSET_TYPES.map((type) => (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, asset_type: type.value })}
                    className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                      formData.asset_type === type.value
                        ? 'border-sky-500 bg-sky-50 ring-1 ring-sky-200'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid={`asset-type-${type.value}`}
                  >
                    <p className="font-medium text-sm">{type.label}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Step 1: Classification */}
        {createStep === 1 && (
          <div className="space-y-4" data-testid="step-classification">
            <Label className="text-sm font-semibold">Criticality Tier</Label>
            <div className="space-y-2">
              {[
                { value: "tier_1", label: "Criticality Tier 1 (Critical)", desc: "Mission-critical assets that must be restored first", color: "border-red-200 bg-red-50" },
                { value: "tier_2", label: "Criticality Tier 2 (Important)", desc: "Important assets needed within hours", color: "border-amber-200 bg-amber-50" },
                { value: "tier_3", label: "Criticality Tier 3 (Standard)", desc: "Standard assets with flexible recovery timelines", color: "border-green-200 bg-green-50" },
              ].map((tier) => (
                <button
                  key={tier.value}
                  type="button"
                  onClick={() => setFormData({ ...formData, manual_tier: tier.value })}
                  className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                    formData.manual_tier === tier.value
                      ? `${tier.color} ring-1 ring-sky-200 border-sky-500`
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                  data-testid={`tier-${tier.value}`}
                >
                  <p className="font-medium text-sm">{tier.label}</p>
                  <p className="text-xs text-slate-500">{tier.desc}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Ownership — V22.9.7 hybrid model */}
        {createStep === 2 && (
          <div className="space-y-4" data-testid="step-ownership">
            {/* Owner — free-text (immediate / operational) */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold flex items-center gap-1">
                Owner
                <Badge variant="outline" className="text-[9px] bg-slate-50 text-slate-500 ml-1">free-text</Badge>
              </Label>
              <Input
                value={formData.owner_name}
                onChange={(e) => setFormData({ ...formData, owner_name: e.target.value })}
                placeholder="Day-to-day operational owner's name"
                data-testid="owner-name-input"
              />
              <Input
                value={formData.owner_email}
                onChange={(e) => setFormData({ ...formData, owner_email: e.target.value })}
                placeholder="Owner email (optional)"
                className="text-sm"
                data-testid="owner-email-input"
              />
            </div>

            {/* Primary Owner — stakeholder picker */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold flex items-center gap-1">
                Primary Owner
                <Badge variant="outline" className="text-[9px] bg-sky-50 text-sky-700 border-sky-200 ml-1">picker</Badge>
              </Label>
              <UserPicker
                api={api}
                value={formData.primary_owner}
                onChange={(u) => setFormData({ ...formData, primary_owner: u })}
                placeholder="Pick primary owner from stakeholder register"
                testIdSuffix="primary-owner"
              />
            </div>

            {/* Secondary Owner — stakeholder picker */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold flex items-center gap-1">
                Secondary Owner
                <Badge variant="outline" className="text-[9px] bg-sky-50 text-sky-700 border-sky-200 ml-1">picker</Badge>
              </Label>
              <UserPicker
                api={api}
                value={formData.secondary_owner}
                onChange={(u) => setFormData({ ...formData, secondary_owner: u })}
                placeholder="Pick backup / secondary owner"
                testIdSuffix="secondary-owner"
              />
            </div>

            {/* Approver — stakeholder picker with role auto-suggest */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold flex items-center gap-1">
                Approver Name
                {formData.approver_role_hint && (
                  <Badge variant="outline" className="text-[10px] bg-violet-50 text-violet-700 border-violet-200 ml-1">
                    Auto-suggest: {(APPROVER_ROLE_BY_ASSET_TYPE[formData.asset_type] || {}).label || "Manager"}
                  </Badge>
                )}
              </Label>
              <UserPicker
                api={api}
                value={formData.approver}
                onChange={(u) =>
                  setFormData({
                    ...formData,
                    approver: u,
                    approver_role_hint: u.role || formData.approver_role_hint,
                  })
                }
                placeholder={`Approver (typically the ${(APPROVER_ROLE_BY_ASSET_TYPE[formData.asset_type] || {}).label || "Asset Manager"})`}
                restrictToRoles={
                  formData.approver_role_hint
                    ? [formData.approver_role_hint, "admin", "bcm_manager"]
                    : null
                }
                testIdSuffix="approver"
              />
              <p className="text-[10.5px] text-slate-500 leading-snug">
                The approver signs off on the asset's criticality, BIA and recovery plans. Defaults to the role manager for this asset type — override if needed.
              </p>
            </div>

            {/* Department */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Department</Label>
              <Input
                value={formData.department}
                onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                placeholder="e.g., IT, Finance, Operations"
                data-testid="department-input"
              />
            </div>
          </div>
        )}

        {/* Step 3: Location */}
        {createStep === 3 && (
          <div className="space-y-4" data-testid="step-location">
            <Label className="flex items-center gap-1 text-sm font-semibold">
              <MapPin className="w-4 h-4" />
              Asset Location *
            </Label>
            <Select 
              value={formData.location_id || "none"} 
              onValueChange={(v) => {
                if (v === "none") {
                  setFormData({ ...formData, location_id: "", location: "" });
                } else {
                  const loc = locations.find(l => l.id === v);
                  setFormData({ ...formData, location_id: v, location: loc?.name || "" });
                }
              }}
            >
              <SelectTrigger className={!formData.location_id ? "border-amber-400" : "border-emerald-400"} data-testid="location-select">
                <SelectValue placeholder="Select asset location" />
              </SelectTrigger>
              <SelectContent>
                {locations.filter(l => l.status !== "retired").length === 0 ? (
                  <SelectItem value="none" disabled>No locations configured. Add in Organization Setup.</SelectItem>
                ) : (
                  locations.filter(l => l.status !== "retired").map(loc => (
                    <SelectItem key={loc.id} value={loc.id}>
                      <div className="flex items-center gap-2">
                        <MapPin className="w-3 h-3 text-slate-400" />
                        {loc.name} - {loc.address?.substring(0, 30)}...
                      </div>
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            {!formData.location_id && (
              <p className="text-xs text-amber-600">Location is required for geographic intelligence</p>
            )}
          </div>
        )}

        {/* Step 4: Review */}
        {createStep === 4 && (
          <div className="space-y-4" data-testid="step-review">
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Description</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of this asset"
                data-testid="description-input"
              />
            </div>
            <div className="p-4 bg-slate-50 rounded-lg border space-y-2">
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Summary</p>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><span className="text-slate-500">Name:</span> <span className="font-medium">{formData.asset_name || "-"}</span></div>
                <div><span className="text-slate-500">Type:</span> <span className="font-medium">{ASSET_TYPES.find(t => t.value === formData.asset_type)?.label || "-"}</span></div>
                <div><span className="text-slate-500">Tier:</span> <span className="font-medium">{formData.manual_tier?.replace("_", " ") || "-"}</span></div>
                <div><span className="text-slate-500">Owner:</span> <span className="font-medium">{formData.owner_name || "-"}</span></div>
                <div><span className="text-slate-500">Primary Owner:</span> <span className="font-medium">{formData.primary_owner?.name || "-"}</span></div>
                <div><span className="text-slate-500">Secondary Owner:</span> <span className="font-medium">{formData.secondary_owner?.name || "-"}</span></div>
                <div className="col-span-2">
                  <span className="text-slate-500">Approver:</span>{" "}
                  <span className="font-medium">{formData.approver?.name || "-"}</span>
                  {formData.approver?.role && (
                    <Badge variant="outline" className="ml-2 text-[10px] bg-violet-50 text-violet-700 border-violet-200">
                      {formData.approver.role.replace(/_/g, " ")}
                    </Badge>
                  )}
                </div>
                <div><span className="text-slate-500">Department:</span> <span className="font-medium">{formData.department || "-"}</span></div>
                <div><span className="text-slate-500">Location:</span> <span className="font-medium">{formData.location || "-"}</span></div>
              </div>
            </div>
          </div>
        )}
      </StepDialog>

      {/* View Asset Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Asset Details</DialogTitle>
          </DialogHeader>
          {selectedAsset && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${getAssetColor(selectedAsset.asset_type)}`}>
                  {React.createElement(getAssetIcon(selectedAsset.asset_type), { className: "w-6 h-6" })}
                </div>
                <div>
                  <h3 className="font-semibold text-lg">{selectedAsset.asset_name}</h3>
                  <p className="text-sm text-slate-500">{selectedAsset.description || "No description"}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-500">Type</p>
                  <p className="font-medium">{ASSET_TYPES.find(t => t.value === selectedAsset.asset_type)?.label}</p>
                </div>
                <div>
                  <p className="text-slate-500">Criticality Tier / Rating</p>
                  <p className="font-medium">{TIER_LABELS[tierKey(selectedAsset.tier)]} (Rating: {selectedAsset.asset_rating})</p>
                </div>
                <div>
                  <p className="text-slate-500">RTO</p>
                  <p className="font-medium">{selectedAsset.rto_hours ? `${selectedAsset.rto_hours} hours` : "Not defined"}</p>
                </div>
                <div>
                  <p className="text-slate-500">RPO</p>
                  <p className="font-medium">{selectedAsset.rpo_hours ? `${selectedAsset.rpo_hours} hours` : "Not defined"}</p>
                </div>
                <div>
                  <p className="text-slate-500">Owner</p>
                  <p className="font-medium">{selectedAsset.owner_name || "Not assigned"}</p>
                </div>
                <div>
                  <p className="text-slate-500">Department</p>
                  <p className="font-medium">{selectedAsset.department || "Not specified"}</p>
                </div>
              </div>

              {/* Reference IDs */}
              {(selectedAsset.dept_id || selectedAsset.process_code || selectedAsset.app_id || selectedAsset.it_function_id) && (
                <div className="pt-2 border-t">
                  <p className="text-sm text-slate-500 mb-2">Reference IDs</p>
                  <div className="grid grid-cols-2 gap-2">
                    {selectedAsset.dept_id && (
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="bg-sky-50 text-sky-700 border-sky-200 font-mono text-xs">{selectedAsset.dept_id}</Badge>
                        <span className="text-xs text-slate-500">Dept</span>
                      </div>
                    )}
                    {selectedAsset.process_code && (
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="bg-violet-50 text-violet-700 border-violet-200 font-mono text-xs">{selectedAsset.process_code}</Badge>
                        <span className="text-xs text-slate-500">Process</span>
                      </div>
                    )}
                    {selectedAsset.app_id && (
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 font-mono text-xs">{selectedAsset.app_id}</Badge>
                        <span className="text-xs text-slate-500">App</span>
                      </div>
                    )}
                    {selectedAsset.it_function_id && (
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="bg-sky-50 text-sky-700 border-sky-200 font-mono text-xs">{selectedAsset.it_function_id}</Badge>
                        <span className="text-xs text-slate-500">IT Function</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="pt-2 border-t">
                <p className="text-sm text-slate-500 mb-2">Plan Coverage</p>
                <div className="flex gap-2">
                  {selectedAsset.has_bcp && <Badge className="bg-blue-100 text-blue-700">BCP</Badge>}
                  {selectedAsset.has_it_drp && <Badge className="bg-purple-100 text-purple-700">IT-DRP</Badge>}
                  {!selectedAsset.has_bcp && !selectedAsset.has_it_drp && (
                    <Badge variant="destructive">No Plans</Badge>
                  )}
                </div>
              </div>

              {selectedAsset.linked_process_name && (
                <div className="pt-2 border-t">
                  <p className="text-sm text-slate-500 mb-2">Linked BIA Process</p>
                  <Badge variant="outline">{selectedAsset.linked_process_name}</Badge>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowViewDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Generate Plan Dialog */}
      <Dialog open={showPlanDialog} onOpenChange={setShowPlanDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Generate Recovery Plan</DialogTitle>
            <DialogDescription>
              Create a BCP or IT-DRP for {selectedAsset?.asset_name}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <Card
              className="cursor-pointer hover:border-blue-500 transition-colors"
              onClick={() => handleGeneratePlan("bcp")}
            >
              <CardContent className="pt-4 flex items-center gap-4">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <Shield className="w-6 h-6 text-blue-700" />
                </div>
                <div>
                  <p className="font-semibold">Business Continuity Plan (BCP)</p>
                  <p className="text-sm text-slate-500">For processes, people, and facilities</p>
                </div>
              </CardContent>
            </Card>
            <Card
              className="cursor-pointer hover:border-purple-500 transition-colors"
              onClick={() => handleGeneratePlan("it_drp")}
            >
              <CardContent className="pt-4 flex items-center gap-4">
                <div className="p-3 bg-purple-100 rounded-lg">
                  <Server className="w-6 h-6 text-purple-700" />
                </div>
                <div>
                  <p className="font-semibold">IT Disaster Recovery Plan (IT-DRP)</p>
                  <p className="text-sm text-slate-500">For IT systems, data, and equipment</p>
                </div>
              </CardContent>
            </Card>
          </div>
          {generating && (
            <div className="flex items-center justify-center py-4">
              <RefreshCw className="w-6 h-6 animate-spin text-sky-700" />
              <span className="ml-2">Generating plan...</span>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dependency Detail Panel */}
      <DependencyDetailPanel
        isOpen={showDependencyPanel}
        onClose={() => {
          setShowDependencyPanel(false);
          setSelectedDependencyItem(null);
        }}
        selectedItem={selectedDependencyItem}
        itemType="asset"
      />

      {/* Link to Application Profile Dialog */}
      <Dialog open={showLinkProfileDialog} onOpenChange={setShowLinkProfileDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="w-5 h-5 text-purple-600" />
              Link Asset to Application Profile
            </DialogTitle>
            <DialogDescription>
              Select an application profile to link with <strong>{assetToLink?.asset_name}</strong>. 
              This will enable bi-directional data sync.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-80 overflow-y-auto space-y-2 py-4">
            {applicationProfiles.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Database className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>No completed application profiles found.</p>
                <p className="text-sm">Complete an application profile wizard first.</p>
              </div>
            ) : (
              applicationProfiles.map((profile) => (
                <Card 
                  key={profile.id}
                  className="cursor-pointer hover:border-purple-400 transition-all"
                  onClick={() => handleLinkToProfile(profile.id)}
                >
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <Server className="w-5 h-5 text-purple-700" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 truncate">{profile.application_name}</p>
                      <p className="text-sm text-slate-500 truncate">
                        {profile.business_owner || "No owner"} • {profile.deployment_type || "Unknown deployment"}
                      </p>
                    </div>
                    <Badge variant="outline" className="bg-purple-50 text-purple-700 flex-shrink-0">
                      {profile.criticality || "Medium"}
                    </Badge>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
          {linking && (
            <div className="flex items-center justify-center py-4 border-t">
              <RefreshCw className="w-5 h-5 animate-spin text-purple-600 mr-2" />
              <span className="text-slate-600">Linking asset...</span>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowLinkProfileDialog(false);
              setAssetToLink(null);
            }}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Column Selection Dialog */}
      <Dialog open={showColumnDialog} onOpenChange={setShowColumnDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Columns className="w-5 h-5 text-sky-700" />
              Manage Visible Columns
            </DialogTitle>
            <DialogDescription>
              Select which database attributes to display as columns. Click on a column to toggle visibility.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {/* Quick Actions */}
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setVisibleColumns(ALL_ASSET_ATTRIBUTES.map(a => a.key))}
              >
                <Check className="w-4 h-4 mr-1" />
                Show All
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setVisibleColumns(ALL_ASSET_ATTRIBUTES.filter(a => a.default).map(a => a.key))}
              >
                Reset to Default
              </Button>
            </div>

            {/* Column Selection Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {ALL_ASSET_ATTRIBUTES.map(attr => {
                const isVisible = visibleColumns.includes(attr.key);
                return (
                  <div
                    key={attr.key}
                    onClick={() => toggleColumn(attr.key)}
                    className={`p-3 rounded-lg border cursor-pointer transition-all ${
                      isVisible 
                        ? 'bg-sky-50 border-sky-300 text-sky-700' 
                        : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm">{attr.name}</p>
                        <p className="text-xs text-slate-400">{attr.type}</p>
                      </div>
                      {isVisible ? (
                        <Check className="w-4 h-4 text-sky-700" />
                      ) : (
                        <div className="w-4 h-4 border rounded" />
                      )}
                    </div>
                    {attr.default && (
                      <Badge variant="outline" className="text-[10px] mt-1">Default</Badge>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="pt-4 border-t text-sm text-slate-500">
              <p><strong>Tip:</strong> Use column filters (in the table header) to filter by specific values. Click column headers to sort.</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
